<template>
  <div class="bg-orange-50/40 rounded-xl border border-orange-200/70 p-5 space-y-4">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-orange-100">
      <div>
        <h3 class="text-base font-bold text-gray-900 flex items-center gap-2">
          <i class="fa fa-utensils text-orange-500" />
          Composición de la Receta / Insumos
        </h3>
        <p class="text-xs text-gray-500 mt-0.5">
          Define los insumos e ingredientes necesarios para preparar 1 porción o unidad de este producto elaborado.
        </p>
      </div>

      <button
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors self-start sm:self-auto"
        @click="addIngredient"
      >
        <i class="fa fa-plus" />
        <span>Agregar Insumo</span>
      </button>
    </div>

    <!-- Empty State -->
    <div
      v-if="!recipeItems.length"
      class="text-center py-8 px-4 border-2 border-dashed border-orange-200 rounded-lg bg-white/70"
    >
      <div class="w-12 h-12 mx-auto mb-2 rounded-full bg-orange-100 text-orange-500 flex items-center justify-center">
        <i class="fa fa-mortar-pestle text-lg" />
      </div>
      <p class="text-sm font-medium text-gray-700">Sin ingredientes asignados aún</p>
      <p class="text-xs text-gray-500 mt-1 max-w-md mx-auto">
        Haz clic en "Agregar Insumo" para empezar a seleccionar los ingredientes (ej: Pan, Carne molida, Queso, Salsas) que se descontarán del inventario en cada venta.
      </p>
      <button
        type="button"
        class="mt-3 inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white border border-orange-300 text-orange-600 hover:bg-orange-50 rounded-lg text-xs font-semibold shadow-sm transition-colors"
        @click="addIngredient"
      >
        <i class="fa fa-plus" />
        <span>Agregar primer ingrediente</span>
      </button>
    </div>

    <!-- Table of Ingredients -->
    <div v-else class="space-y-3">
      <div class="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table class="w-full text-left text-xs divide-y divide-gray-100">
          <thead class="bg-gray-50 text-gray-600 font-semibold uppercase tracking-wider">
            <tr>
              <th class="py-2.5 px-3">Insumo / Ingrediente *</th>
              <th class="py-2.5 px-3 w-28">Cant. Requerida *</th>
              <th class="py-2.5 px-3 w-28">Unidad (Opc.)</th>
              <th class="py-2.5 px-3 w-24 text-right">Costo Unit.</th>
              <th class="py-2.5 px-3 w-28 text-right">Subtotal Costo</th>
              <th class="py-2.5 px-2 w-12 text-center" />
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="(item, index) in recipeItems"
              :key="index"
              class="hover:bg-orange-50/20 transition-colors"
            >
              <!-- Ingredient Select -->
              <td class="p-2.5">
                <select
                  v-model="item.ingredient_id"
                  class="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-md text-xs focus:ring-orange-500 focus:border-orange-500 transition-colors"
                  required
                  @change="onIngredientSelected(item)"
                >
                  <option value="">Selecciona un insumo...</option>
                  <optgroup v-if="insumoList.length" label="Insumos / Materias Primas">
                    <option
                      v-for="ing in insumoList"
                      :key="'insumo-' + ing.id"
                      :value="ing.id"
                    >
                      {{ ing.code ? '[' + ing.code + '] ' : '' }}{{ ing.name }} (Costo: ${{ Number(ing.cost || 0).toFixed(2) }})
                    </option>
                  </optgroup>
                  <optgroup v-if="otherProductsList.length" label="Otros Productos">
                    <option
                      v-for="ing in otherProductsList"
                      :key="'other-' + ing.id"
                      :value="ing.id"
                    >
                      {{ ing.code ? '[' + ing.code + '] ' : '' }}{{ ing.name }} (Costo: ${{ Number(ing.cost || 0).toFixed(2) }})
                    </option>
                  </optgroup>
                </select>
              </td>

              <!-- Quantity Input -->
              <td class="p-2.5">
                <input
                  v-model.number="item.quantity"
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  placeholder="Ej: 1 o 0.150"
                  class="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-md text-xs focus:ring-orange-500 focus:border-orange-500"
                  required
                  @input="recalculate"
                />
              </td>

              <!-- Unit Input -->
              <td class="p-2.5">
                <input
                  v-model="item.unit"
                  type="text"
                  placeholder="Ej: kg, unid, gr"
                  class="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-md text-xs focus:ring-orange-500 focus:border-orange-500"
                />
              </td>

              <!-- Unit Cost -->
              <td class="p-2.5 text-right font-medium text-gray-600">
                ${{ Number(item.cost || 0).toFixed(2) }}
              </td>

              <!-- Subtotal Cost -->
              <td class="p-2.5 text-right font-bold text-gray-900">
                ${{ (Number(item.quantity || 0) * Number(item.cost || 0)).toFixed(2) }}
              </td>

              <!-- Remove -->
              <td class="p-2.5 text-center">
                <button
                  type="button"
                  class="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                  title="Eliminar ingrediente"
                  @click="removeIngredient(index)"
                >
                  <i class="fa fa-trash-alt text-xs" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cost Summary Bar -->
      <div class="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 bg-white rounded-lg border border-orange-200 shadow-xs">
        <div class="flex items-center gap-2 text-xs text-gray-600">
          <i class="fa fa-calculator text-orange-500" />
          <span>Ingredientes agregados: <strong class="text-gray-900">{{ recipeItems.length }}</strong></span>
        </div>

        <div class="flex items-center gap-4">
          <div class="text-right">
            <span class="block text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Costo Total Receta:</span>
            <span class="text-base font-extrabold text-orange-600">${{ totalRecipeCost.toFixed(2) }}</span>
          </div>

          <button
            type="button"
            class="px-2.5 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-md text-xs font-medium transition-colors"
            title="Actualizar costo del producto con la suma de la receta"
            @click="syncCostToParent"
          >
            <i class="fa fa-sync-alt mr-1 text-[10px]" />
            Fijar como Costo
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RecipeBuilder',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    insumos: {
      type: Array,
      default: () => [],
    },
    availableIngredients: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:items', 'cost-changed'],
  data() {
    return {
      recipeItems: [],
    }
  },
  computed: {
    allIngredients() {
      if (this.availableIngredients && this.availableIngredients.length) {
        return this.availableIngredients
      }
      return this.insumos || []
    },
    insumoList() {
      return this.allIngredients.filter(i => i.type_product === 'insumo')
    },
    otherProductsList() {
      return this.allIngredients.filter(i => i.type_product !== 'insumo')
    },
    totalRecipeCost() {
      return this.recipeItems.reduce((acc, item) => {
        const qty = Number(item.quantity) || 0
        const cost = Number(item.cost) || 0
        return acc + (qty * cost)
      }, 0)
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(newVal) {
        if (newVal && Array.isArray(newVal)) {
          this.recipeItems = newVal.map(item => {
            const found = this.allIngredients.find(ing => ing.id === item.ingredient_id)
            return {
              ingredient_id: item.ingredient_id || '',
              quantity: item.quantity !== undefined ? item.quantity : 1,
              unit: item.unit || '',
              cost: item.cost !== undefined ? item.cost : (found?.cost || 0),
            }
          })
        }
      },
    },
  },
  methods: {
    addIngredient() {
      this.recipeItems.push({
        ingredient_id: '',
        quantity: 1,
        unit: 'unidad',
        cost: 0,
      })
      this.emitChanges()
    },
    removeIngredient(index) {
      this.recipeItems.splice(index, 1)
      this.emitChanges()
    },
    onIngredientSelected(item) {
      const found = this.allIngredients.find(ing => ing.id === item.ingredient_id)
      if (found) {
        item.cost = Number(found.cost || 0)
      } else {
        item.cost = 0
      }
      this.emitChanges()
    },
    recalculate() {
      this.emitChanges()
    },
    emitChanges() {
      this.$emit('update:items', this.recipeItems)
      this.$emit('cost-changed', this.totalRecipeCost)
    },
    syncCostToParent() {
      this.$emit('cost-changed', this.totalRecipeCost)
    },
  },
}
</script>
